import React, { useRef, useEffect, useState } from "react";
import Lottie from "lottie-react";

export default function ScrollLottie({
    animationData,
    scrollIndicatorData, // 👈 NEW: Lottie JSON for scroll-down indicator
    heading,
    accentColor = "#7B3F00",
    scrollLength = 400,
    widthClass = "w-[60%]",
    maxWidthClass = "max-w-[600px]",
}) {
    const containerRef = useRef(null);
    const lottieRef = useRef(null);
    const [progress, setProgress] = useState(0);

    // --- Scroll tracking ---
    useEffect(() => {
        const handleScroll = () => {
            if (!containerRef.current) return;

            const rect = containerRef.current.getBoundingClientRect();
            const windowHeight = window.innerHeight - 500;
            const totalScroll = rect.height - windowHeight;
            const scrolled = Math.min(
                Math.max(windowHeight - rect.top, 0),
                rect.height
            );
            const percent = scrolled / (totalScroll + windowHeight);
            setProgress(percent);
        };

        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    // --- Frame scrubbing ---
    useEffect(() => {
        if (!lottieRef.current) return;
        const instance = lottieRef.current;
        const totalFrames = instance.getDuration(true);
        const frame = progress * totalFrames;

        if (progress >= 1) {
            instance.goToAndStop(totalFrames - 1, true);
        } else {
            instance.goToAndStop(frame, true);
        }
    }, [progress]);

    // --- Skip handler ---
    const handleSkip = () => {
        if (!containerRef.current) return;
        const offsetTop = containerRef.current.offsetTop;
        const skipTo = offsetTop + window.innerHeight * (scrollLength / 100);
        window.scrollTo({
            top: skipTo,
            behavior: "smooth",
        });
    };

    return (
        <div ref={containerRef} className="relative w-full">
            <div
                className="relative bg-[#fcf3e7]"
                style={{ height: `${scrollLength}vh` }}
            >
                {/* Sticky animation */}
                <div className="sticky top-0 h-screen flex flex-col items-center justify-center relative">
                    {/* Heading */}
                    {heading && (
                        <h2
                            className="text-center text-3xl sm:text-5xl font-bold mb-6"
                            style={{ color: accentColor }}
                        >
                            {heading}
                        </h2>
                    )}

                    {/* Main Lottie */}
                    <div className={`${widthClass} ${maxWidthClass} mx-auto`}>
                        <Lottie
                            lottieRef={lottieRef}
                            animationData={animationData}
                            loop={false}
                            autoplay={false}
                        />
                    </div>

                    {/* ⬇️ Scroll-down Lottie button (NO TEXT) */}
                    {scrollIndicatorData && progress < 0.95 && (
                        <button
                            onClick={handleSkip}
                            aria-label="Scroll down"
                            className="absolute bottom-6 left-1/2 -translate-x-1/2 w-20 h-20 cursor-pointer"
                        >
                            <Lottie
                                animationData={scrollIndicatorData}
                                loop
                                autoplay
                                style={{ width: "100%", height: "100%" }}
                            />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
